import React, { useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Typography,
    Chip,
    Box,
    Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    selectFilteredInstanceTypes,
    selectPolicyConstraintsByProviderAndPolicy,
    selectConfigurationValidationById
} from 'app/main/cluster/store/selectors';

const useStyles = makeStyles((theme) => ({
    formControl: {
        width: '100%',
        minWidth: 200
    },
    menuItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: theme.spacing(1, 2),
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    instanceName: {
        fontWeight: 600,
        fontSize: '0.95rem'
    },
    instanceSpecs: {
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        marginTop: 2
    },
    constraintChip: {
        marginLeft: theme.spacing(1),
        height: 20,
        fontSize: '0.75rem'
    },
    validationError: {
        color: theme.palette.error.main
    },
    validationSuccess: {
        color: theme.palette.success.main
    },
    disabledOption: {
        opacity: 0.5,
        '&:hover': {
            backgroundColor: 'transparent !important'
        }
    }
}));

const InstanceTypeSelector = ({
    value,
    onChange,
    disabled = false,
    size = 'small',
    label = 'Instance Type',
    providerId,
    selectedPolicy,
    availableInstances = [],
    required = false,
    error = false,
    helperText = '',
    showConstraints = true,
    ...props
}) => {
    const classes = useStyles();
    const lastAutoSelectedRef = useRef(null);
    const lastSelectedPolicyRef = useRef(selectedPolicy);
    
    // Reset auto-selection tracking when policy changes
    if (lastSelectedPolicyRef.current !== selectedPolicy) {
        lastSelectedPolicyRef.current = selectedPolicy;
        lastAutoSelectedRef.current = null;
    }

    // Get policy constraints and validation from Redux
    const policyConstraints = useSelector(state => 
        providerId && selectedPolicy ? selectPolicyConstraintsByProviderAndPolicy(state, providerId, selectedPolicy) : null
    );
    const configurationValidation = useSelector(state =>
        providerId ? selectConfigurationValidationById(state, providerId) : null
    );

    // Filter instances based on policy constraints
    const filteredInstances = useMemo(() => {
        if (!selectedPolicy || selectedPolicy === 'custom' || !policyConstraints) {
            return availableInstances;
        }

        const allowedInstanceTypes = policyConstraints.allowedConfigurations?.map(
            config => config.instanceType
        ) || [];

        if (allowedInstanceTypes.length === 0) {
            return availableInstances;
        }

        const filtered = availableInstances.filter(instance => {
            const instanceType = instance.instance_id || instance.type || instance.name;
            return allowedInstanceTypes.includes(instanceType);
        });
        
        return filtered;
    }, [availableInstances, selectedPolicy, policyConstraints]);

    // Auto-select first valid instance if current selection is not allowed by policy or no selection exists
    useLayoutEffect(() => {
        if (filteredInstances.length > 0 && onChange) {
            // Check if we need to auto-select (no value or current value not in filtered list)
            const currentValueInFiltered = value && filteredInstances.some(instance => 
                (instance.instance_id || instance.type || instance.name) === value
            );
            const shouldAutoSelect = !value || !currentValueInFiltered;
            const firstValidInstance = filteredInstances[0];
            const firstValidType = firstValidInstance.instance_id || firstValidInstance.type || firstValidInstance.name;
            
            // Only auto-select if we haven't already auto-selected this value
            if (shouldAutoSelect && firstValidType !== lastAutoSelectedRef.current) {
                
                lastAutoSelectedRef.current = firstValidType;
                
                // Call onChange immediately in layout effect
                const syntheticEvent = { target: { value: firstValidType } };
                onChange(syntheticEvent, {
                    value: firstValidType,
                    isValid: true,
                    instance: firstValidInstance
                });
            }
        }
    }, [filteredInstances, value, onChange]);

    // Check if current selection is valid according to policy
    const isCurrentSelectionValid = useMemo(() => {
        if (!value || !selectedPolicy || selectedPolicy === 'custom') {
            return true;
        }

        if (!policyConstraints?.allowedConfigurations) {
            return true;
        }

        const allowedInstanceTypes = policyConstraints.allowedConfigurations.map(
            config => config.instanceType
        );

        return allowedInstanceTypes.includes(value);
    }, [value, selectedPolicy, policyConstraints]);

    // Determine helper text based on validation state
    const effectiveHelperText = useMemo(() => {
        if (helperText) {
            return helperText;
        }

        if (!selectedPolicy || selectedPolicy === 'custom') {
            return 'Any instance type available';
        }

        if (policyConstraints && filteredInstances.length > 0) {
            const constraintCount = policyConstraints.allowedConfigurations?.length || 0;
            return `${filteredInstances.length} instance types allowed by ${selectedPolicy} policy (${constraintCount} configurations)`;
        }

        if (policyConstraints && filteredInstances.length === 0) {
            return 'No instance types match the selected policy constraints';
        }

        return 'Loading policy constraints...';
    }, [helperText, selectedPolicy, policyConstraints, filteredInstances.length]);

    // Handle change with validation
    const handleChange = (event) => {
        const selectedValue = event.target.value;
        
        // Reset the auto-selected ref when user manually changes the value
        lastAutoSelectedRef.current = null;
        
        if (onChange) {
            onChange(event, {
                value: selectedValue,
                isValid: isCurrentSelectionValid,
                instance: filteredInstances.find(inst => 
                    (inst.instance_id || inst.type || inst.name) === selectedValue
                )
            });
        }
    };

    // Format instance display name
    const formatInstanceDisplay = (instance) => {
        const type = instance.instance_id || instance.type || instance.name;
        const specs = [];
        
        // Check multiple possible field names for CPU (vcpu, vCPU, cpu, CPUs)
        const cpuValue = instance.vcpu || instance.vCPU || instance.cpu || instance.CPUs;
        if (cpuValue) {
            specs.push(`${cpuValue} vCPU`);
        }
        
        // Check multiple possible field names for memory (memory, Memory, ram, RAM)
        const memoryValue = instance.memory || instance.Memory || instance.ram || instance.RAM;
        if (memoryValue) {
            specs.push(`${memoryValue} GB RAM`);
        }
        
        // Check multiple possible field names for storage
        const storageValue = instance.storage || instance.Storage || instance.disk;
        if (storageValue) {
            // Remove 'GB' if it's already in the string to avoid duplication
            const storageStr = String(storageValue).replace(/\s*GB\s*/i, '');
            specs.push(`${storageStr} Storage`);
        }

        return {
            type,
            name: instance.name || type,
            specs: specs.join(', ')
        };
    };

    return (
        <FormControl 
            className={classes.formControl} 
            size={size} 
            error={error || !isCurrentSelectionValid}
            disabled={disabled}
            required={required}
            {...props}
        >
            <InputLabel>{label}</InputLabel>
            <Select
                value={(() => {
                    const val = value || '';
                    return val;
                })()}
                onChange={handleChange}
                displayEmpty
            >
                {filteredInstances.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="textSecondary">
                            {selectedPolicy && selectedPolicy !== 'custom' 
                                ? 'No instances match policy constraints'
                                : 'No instances available'
                            }
                        </Typography>
                    </MenuItem>
                ) : (
                    filteredInstances.map((instance) => {
                        const { type, name, specs } = formatInstanceDisplay(instance);
                        const isSelected = value === type;
                        
                        return (
                            <MenuItem 
                                key={type} 
                                value={type}
                                className={classes.menuItem}
                            >
                                <Box display="flex" alignItems="center" width="100%">
                                    <Box flex={1}>
                                        <Typography className={classes.instanceName}>
                                            {name}
                                        </Typography>
                                        {specs && (
                                            <Typography className={classes.instanceSpecs}>
                                                {specs}
                                            </Typography>
                                        )}
                                    </Box>
                                    {showConstraints && selectedPolicy && selectedPolicy !== 'custom' && (
                                        <Chip
                                            label="Policy Match"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            className={classes.constraintChip}
                                        />
                                    )}
                                </Box>
                            </MenuItem>
                        );
                    })
                )}
            </Select>
            
            <FormHelperText 
                className={
                    !isCurrentSelectionValid 
                        ? classes.validationError 
                        : configurationValidation?.isValid 
                            ? classes.validationSuccess 
                            : undefined
                }
            >
                {!isCurrentSelectionValid 
                    ? `Selected instance type not allowed by ${selectedPolicy} policy`
                    : effectiveHelperText
                }
            </FormHelperText>
        </FormControl>
    );
};

export default InstanceTypeSelector;
